import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard,
  Users,
  Calendar,
  Grid,
  ShieldCheck,
  Star,
  Percent,
  AlertTriangle,
  LogOut,
  ChevronRight,
  Menu,
  X,
  Shield
} from "lucide-react";
import LanguageSwitcher from "./LanguageSwitcher";
import NotificationBell from "./NotificationBell";

export default function AdminSidebar({ activeTab = "" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const navItems = [
    {
      id: "overview",
      label: t("admin.dashboardTitle", "Panel General"),
      icon: LayoutDashboard,
      path: "/admin/dashboard",
      color: "text-[#134168]",
      bgColor: "bg-blue-100"
    },
    {
      id: "users",
      label: t("admin.usersTitle", "Usuarios"),
      icon: Users,
      path: "/admin/users",
      color: "text-indigo-600",
      bgColor: "bg-indigo-100"
    },
    {
      id: "bookings",
      label: t("admin.bookingsTitle", "Reservas"),
      icon: Calendar,
      path: "/admin/bookings",
      color: "text-[#36C0C7]",
      bgColor: "bg-teal-100"
    },
    {
      id: "categories",
      label: t("admin.categoriesTitle", "Categorías"),
      icon: Grid,
      path: "/admin/categories",
      color: "text-emerald-600",
      bgColor: "bg-emerald-100"
    },
    {
      id: "client-verification",
      label: t("admin.clientVerificationTitle", "Verificaciones"),
      icon: ShieldCheck,
      path: "/admin/client-verification",
      color: "text-blue-600",
      bgColor: "bg-blue-100"
    },
    {
      id: "reviews",
      label: t("admin.reviewsTitle", "Reseñas"),
      icon: Star,
      path: "/admin/reviews",
      color: "text-amber-500",
      bgColor: "bg-amber-100"
    },
    {
      id: "commission-config",
      label: t("admin.commissionTitle", "Comisiones"),
      icon: Percent,
      path: "/admin/commission-config",
      color: "text-purple-600",
      bgColor: "bg-purple-100"
    },
    {
      id: "disputes",
      label: t("admin.disputesTitle", "Disputas"),
      icon: AlertTriangle,
      path: "/admin/disputes",
      color: "text-rose-600",
      bgColor: "bg-rose-100"
    }
  ];

  return (
    <>
      {/* Botón flotante móvil */}
      <button
        onClick={() => setIsOpen(true)}
        aria-label="Abrir menú de administración"
        className={`fixed bottom-6 right-6 z-40 md:hidden bg-[#134168] text-white p-4 rounded-full shadow-2xl transition-transform active:scale-95 ${
          isOpen ? "hidden" : "block"
        }`}
      >
        <Menu className="w-6 h-6" />
      </button>

      {/* Backdrop para móvil */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-50 md:hidden backdrop-blur-sm transition-opacity"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar Aside */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-80 max-w-[85vw] bg-white border-r border-gray-200 shadow-xl flex flex-col transition-transform duration-300 ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        } md:translate-x-0 md:static md:z-10 md:min-h-screen shrink-0`}
      >
        {/* Header / Logo */}
        <div className="p-6 border-b flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 font-bold text-xl text-[#134168] hover:opacity-90 transition-opacity"
          >
            <Shield className="w-6 h-6 text-[#36C0C7]" />
            <span>RedMobility <span className="text-xs bg-[#134168] text-white px-2 py-0.5 rounded-full ml-1 font-semibold">Admin</span></span>
          </button>
          <button
            onClick={() => setIsOpen(false)}
            className="md:hidden p-2 rounded-xl text-gray-500 hover:bg-gray-100"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tarjeta del Administrador */}
        <div className="p-5">
          <div className="bg-gradient-to-br from-gray-900 via-[#134168] to-[#36C0C7] rounded-3xl p-6 text-white shadow-md">
            <div className="w-16 h-16 rounded-full bg-white/20 mx-auto flex items-center justify-center text-2xl font-bold mb-3 shadow-inner">
              {user?.name?.[0]?.toUpperCase() || "A"}
            </div>
            <h2 className="text-center font-bold text-lg truncate">
              {user?.name || "Administrador"}
            </h2>
            <p className="text-center text-xs text-white/80 truncate mt-0.5">
              {user?.email}
            </p>
            <div className="flex justify-center mt-3">
              <span className="bg-white/20 px-3 py-0.5 rounded-full text-xs font-semibold tracking-wide">
                Administrador Global
              </span>
            </div>
          </div>
        </div>

        {/* Links de Navegación */}
        <div className="flex-1 px-4 py-2 space-y-1.5 overflow-y-auto">
          <p className="text-xs uppercase tracking-wider text-gray-400 font-bold mb-2 px-2">
            Administración
          </p>

          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || activeTab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => {
                  navigate(item.path);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center justify-between rounded-2xl p-3 transition-all group ${
                  isActive
                    ? "bg-[#EEF6FD] text-[#134168] font-bold shadow-sm"
                    : "hover:bg-gray-50 text-gray-700"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-xl ${item.bgColor} flex items-center justify-center shrink-0`}>
                    <Icon className={`w-4 h-4 ${item.color}`} />
                  </div>
                  <span className="text-sm font-medium">{item.label}</span>
                </div>
                <ChevronRight
                  className={`w-4 h-4 transition-transform group-hover:translate-x-0.5 ${
                    isActive ? "text-[#134168]" : "text-gray-400"
                  }`}
                />
              </button>
            );
          })}

          <div className="pt-3 border-t border-gray-100 space-y-2">
            <div className="w-full flex items-center justify-between rounded-2xl p-3 bg-gray-50">
              <span className="text-xs font-semibold text-gray-600">Idioma</span>
              <LanguageSwitcher />
            </div>
            <div className="w-full flex items-center justify-between rounded-2xl p-3 bg-gray-50">
              <span className="text-xs font-semibold text-gray-600">Alertas</span>
              <NotificationBell />
            </div>
          </div>
        </div>

        {/* Footer Cerrar Sesión */}
        <div className="p-4 border-t border-gray-100">
          <button
            onClick={handleLogout}
            className="w-full bg-red-50 hover:bg-red-100 text-red-600 rounded-2xl p-3 font-bold text-sm flex items-center justify-center gap-2 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            <span>{t("navbar.logout", "Cerrar sesión")}</span>
          </button>
        </div>
      </aside>
    </>
  );
}
